import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, UserPlus, CheckCircle2, AlertCircle, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Page "Inscription rapide sur place" — réservée aux rôles Admin / Responsable / Protocole / Sécurité.
 * Formulaire ultra-court : prénom · nom · téléphone · email → QR généré immédiatement.
 */
export default function QuickRegisterPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [eventSlug, setEventSlug] = useState("");
  const [stage, setStage] = useState("");
  const [stages, setStages] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // success result

  useEffect(() => {
    if (!user) nav("/admin/connexion");
    else if (!["admin", "responsable", "protocole", "securite"].includes(user.role)) nav("/");
  }, [user, nav]);

  useEffect(() => {
    api.get("/admin/events").then((r) => {
      setEvents(r.data);
      const feat = r.data.find((e) => e.is_featured) || r.data[0];
      if (feat) {
        setEventId(feat.id); setEventSlug(feat.slug);
        setStages(feat.stages || []);
      }
    });
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/quick-register", {
        event_slug: eventSlug,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        selected_stage: stage || null,
      });
      setResult(r.data);
      setForm({ first_name: "", last_name: "", phone: "", email: "" });
      toast.success("Inscription enregistrée");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const newOne = () => setResult(null);

  return (
    <div className="min-h-screen bg-ink text-white" data-testid="quick-register-page">
      <header className="px-4 sm:px-6 py-4 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 grid place-items-center border border-white/15 hover:border-gold hover:text-gold rounded-sm">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.2em] text-gold">Sur place · Protocole & Sécurité</div>
            <div className="font-serif text-lg leading-tight">Inscription rapide</div>
          </div>
        </div>
        <Link to="/admin/scan" className="text-xs text-gold hover:underline flex items-center gap-1.5">
          <QrCode className="w-3.5 h-3.5" /> Scanner
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {result ? (
          <div className="bg-emerald-500 text-white p-6 rounded-sm shadow-xl" data-testid="quick-register-success">
            <div className="flex items-start gap-4">
              <CheckCircle2 className="w-14 h-14 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-3xl font-bold tracking-wider">INSCRIT</div>
                <div className="text-sm opacity-90 mt-1">QR code généré · email envoyé</div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm bg-white/10 p-4 rounded-sm">
                  <div><span className="opacity-75 text-xs uppercase tracking-wider">Nom</span><br/>{result.first_name} {result.last_name}</div>
                  <div><span className="opacity-75 text-xs uppercase tracking-wider">N°</span><br/><span className="font-mono">{result.registration_number}</span></div>
                  <div><span className="opacity-75 text-xs uppercase tracking-wider">Email</span><br/>{result.email}</div>
                  <div><span className="opacity-75 text-xs uppercase tracking-wider">Téléphone</span><br/>{result.phone}</div>
                </div>
                {result.qr_data_url && (
                  <div className="mt-4 text-center">
                    <img src={result.qr_data_url} alt="QR" className="w-40 h-40 mx-auto bg-white p-2 rounded-sm" />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={newOne} className="flex-1 bg-white text-emerald-700 font-bold py-3 rounded-sm hover:bg-white/95" data-testid="quick-register-new">
                <UserPlus className="w-4 h-4 inline mr-1" /> Nouvelle inscription
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white/5 border border-white/10 rounded-sm p-6 space-y-4" data-testid="quick-register-form">
            <div>
              <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/60 mb-1.5">Événement</label>
              <select value={eventId} onChange={(e) => {
                const ev = events.find((x) => x.id === e.target.value);
                setEventId(e.target.value);
                setEventSlug(ev?.slug || "");
                setStages(ev?.stages || []);
                setStage("");
              }} data-testid="quick-register-event"
                className="w-full bg-ink border border-white/15 rounded-sm p-3 text-sm">
                {events.map((e) => <option key={e.id} value={e.id} className="text-ink">{e.title}</option>)}
              </select>
            </div>
            {stages.length > 0 && (
              <div>
                <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/60 mb-1.5">Étape / Ville</label>
                <select value={stage} onChange={(e) => setStage(e.target.value)} data-testid="quick-register-stage"
                  className="w-full bg-ink border border-white/15 rounded-sm p-3 text-sm">
                  <option value="" className="text-ink">— Non précisée —</option>
                  {stages.map((s, i) => <option key={i} value={s.city || s.name || s} className="text-ink">{s.city || s.name || s}</option>)}
                </select>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Prénom" v={form.first_name} on={(v) => setForm({ ...form, first_name: v })} test="quick-fn" />
              <Field label="Nom" v={form.last_name} on={(v) => setForm({ ...form, last_name: v })} test="quick-ln" />
            </div>
            <Field label="Téléphone" type="tel" v={form.phone} on={(v) => setForm({ ...form, phone: v })} test="quick-phone" />
            <Field label="Email" type="email" v={form.email} on={(v) => setForm({ ...form, email: v })} test="quick-email" />

            <button disabled={loading} className="w-full bg-gold text-ink font-bold py-4 rounded-sm hover:bg-gold/90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="quick-register-submit">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <UserPlus className="w-5 h-5" />}
              {loading ? "Enregistrement…" : "Enregistrer & générer QR"}
            </button>
            <div className="text-[0.65rem] text-white/55 flex items-start gap-1.5">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>L'inscrit reçoit automatiquement un email de confirmation avec son QR code.</span>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({ label, v, on, test, type = "text" }) {
  return (
    <div>
      <label className="block text-[0.6rem] uppercase tracking-[0.2em] text-white/60 mb-1.5">{label}</label>
      <input required type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={test}
             className="w-full bg-ink border border-white/15 rounded-sm p-3 text-sm focus:border-gold outline-none" />
    </div>
  );
}
